import React, { useMemo } from 'react';
import { Calendar, ChevronLeft, ChevronRight } from 'lucide-react';
import { formatForDisplay, getRelativeTime, parseFromDateTimeInput } from '../../../utils/timezoneUtils';
import { getStatusBadgeColor } from './dashboardUtils';

const TimelineView = ({ projects = [], onSelectProject }) => {
  // Calculate 30-day forward timeline
  const timelineData = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const startDate = new Date(today); // Today
    
    const endDate = new Date(today);
    endDate.setDate(today.getDate() + 30); // 30 days forward
    
    // Generate date labels for headers
    const dateLabels = [];
    for (let i = 0; i < 30; i++) {
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + i);
      const isToday = date.toDateString() === today.toDateString();
      const isWeekend = date.getDay() === 0 || date.getDay() === 6;
      
      dateLabels.push({
        date,
        label: date.getDate().toString(),
        isToday,
        isWeekend,
        position: 5 + (i / 29) * 90 // 30 days = positions 5%-95%
      });
    }

    // Calculate project bars
    const scheduledProjects = projects.filter(p => (p.start_datetime || p.end_datetime) && !['completed', 'cancelled'].includes(p.status)).map(project => {
      // Handle different date formats - could be ISO string or datetime-local format
      let projectStart = startDate;
      let projectEnd = endDate;
      
      if (project.start_datetime) {
        const parsed = parseFromDateTimeInput(project.start_datetime);
        projectStart = parsed ? parsed : new Date(project.start_datetime);
      }
      
      if (project.end_datetime) {
        const parsed = parseFromDateTimeInput(project.end_datetime);
        projectEnd = parsed ? parsed : new Date(project.end_datetime);
      }
      
      // Ensure we have valid Date objects
      if (!(projectStart instanceof Date) || isNaN(projectStart)) {
        projectStart = startDate;
      }
      if (!(projectEnd instanceof Date) || isNaN(projectEnd)) {
        projectEnd = endDate;
      }
      
      // Calculate position and width as percentage
      const timelineStart = startDate.getTime();
      const timelineEnd = endDate.getTime();
      const timelineRange = timelineEnd - timelineStart;
      
      const barStart = Math.max(projectStart.getTime(), timelineStart);
      const barEnd = Math.min(projectEnd.getTime(), timelineEnd);
      
      const left = 5 + ((barStart - timelineStart) / timelineRange) * 90;
      const width = ((barEnd - barStart) / timelineRange) * 90;
      
      const startsBeforeTimeline = projectStart.getTime() < timelineStart;
      const endsAfterTimeline = projectEnd.getTime() > timelineEnd;
      
      return {
        ...project,
        barMetrics: {
          left: Math.max(0, left),
          width: Math.max(1, width),
          startsBeforeTimeline,
          endsAfterTimeline
        }
      };
    });

    return {
      startDate,
      endDate,
      dateLabels,
      scheduledProjects
    };
  }, [projects]);

  const unscheduledProjects = projects.filter(p => !p.start_datetime && !p.end_datetime);

  return (
    <div className="flex-1 bg-slate-900 flex flex-col">
      {/* Timeline Header */}
      <div className="bg-slate-800 border-b border-slate-700 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-1.5 bg-blue-900/30 border border-blue-700/50 rounded">
              <Calendar className="w-4 h-4 text-blue-400" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white">Project Timeline</h2>
              <p className="text-xs text-slate-400">
                {formatForDisplay(timelineData.startDate)} - {formatForDisplay(timelineData.endDate)}
              </p>
            </div>
          </div>
          <div className="text-xs text-slate-400">
            {timelineData.scheduledProjects.length} scheduled projects
          </div>
        </div>
      </div>

      {/* Timeline Content */}
      <div className="flex-1 overflow-auto min-w-0 custom-scrollbar">
        {timelineData.scheduledProjects.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <div className="w-16 h-16 bg-slate-700 rounded-full flex items-center justify-center mx-auto mb-4">
                <Calendar className="w-8 h-8 text-slate-400" />
              </div>
              <h3 className="text-lg font-medium text-slate-200 mb-2">No Scheduled Projects</h3>
              <p className="text-slate-400">
                Projects with start and end dates will appear in the timeline view.
              </p>
            </div>
          </div>
        ) : (
          <div className="relative bg-slate-900 min-h-full">
            {/* Global Timeline Grid Lines */}
            <div className="absolute inset-0 pointer-events-none" style={{ top: '48px' }}>
              {timelineData.dateLabels.map((label, i) => (
                <div 
                  key={i} 
                  className={`absolute top-0 bottom-0 w-px ${
                    label.isToday ? 'bg-blue-400/40' : 'bg-slate-700/30'
                  }`} 
                  style={{ left: `${label.position}%` }} 
                />
              ))}
            </div>

            {/* Date Headers */}
            <div className="sticky top-0 z-10 h-12 bg-slate-800/95 backdrop-blur-sm border-b border-slate-700">
              <div className="relative h-full w-full">
                {timelineData.dateLabels.map((label, index) => (
                  <div
                    key={index}
                    className="absolute top-0 h-full flex flex-col items-center justify-center transform -translate-x-1/2"
                    style={{ left: `${label.position}%` }}
                  >
                    <div className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                      label.isToday
                        ? 'bg-blue-500 text-white'
                        : label.isWeekend
                        ? 'bg-amber-500/20 text-amber-300'
                        : 'text-slate-300'
                    }`}>
                      {label.isToday ? 'TODAY' : label.label}
                    </div>
                    <span className={`text-xs mt-0.5 ${
                      label.isToday ? 'text-blue-300' : 'text-slate-500'
                    }`}>
                      {label.date.toLocaleDateString('en-US', { month: 'short' })}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Project Rows */}
            <div className="pt-4 pb-6">
              {timelineData.scheduledProjects.map((project, index) => {
                const completedTasks = project.completed_tasks || 0;
                const totalTasks = project.total_tasks || 0;
                const progressPercentage = totalTasks > 0 ? (completedTasks / totalTasks) * 100 : 0;

                return (
                  <div 
                    key={project.id} 
                    className="relative h-16 mb-2 hover:bg-slate-800/10 transition-colors duration-200"
                  >
                    {/* Project Timeline Bar */}
                    <div
                      className={`absolute top-4 h-10 rounded cursor-pointer transition-all duration-200 shadow hover:shadow-lg group ${
                        project.status === 'completed'
                          ? 'bg-green-600/80 border-green-500'
                          : project.status === 'in_progress'
                          ? 'bg-blue-600/80 border-blue-500'
                          : project.status === 'on_hold'
                          ? 'bg-amber-600/80 border-amber-500'
                          : project.status === 'cancelled'
                          ? 'bg-red-600/80 border-red-500'
                          : 'bg-slate-600/80 border-slate-500'
                      } border overflow-hidden`}
                      style={{
                        left: `${project.barMetrics.left}%`,
                        width: `${project.barMetrics.width}%`,
                        minWidth: '60px'
                      }}
                      onClick={() => onSelectProject(project)}
                    >
                      {/* Progress overlay */}
                      {totalTasks > 0 && (
                        <div 
                          className="absolute inset-0 bg-white/10 transition-all duration-300" 
                          style={{ width: `${progressPercentage}%` }} 
                        />
                      )}
                      
                      {/* Bar content - just project title */}
                      <div className="absolute inset-0 flex items-center px-3">
                        <div className="font-medium text-white text-sm truncate">
                          {project.name}
                        </div>
                      </div>
                      
                      {/* Timeline overflow indicators */}
                      {project.barMetrics.startsBeforeTimeline && (
                        <div className="absolute left-0 top-0 bottom-0 w-1 bg-white/60" />
                      )}
                      {project.barMetrics.endsAfterTimeline && (
                        <div className="absolute right-0 top-0 bottom-0 w-1 bg-white/60" />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Unscheduled Projects */}
        {unscheduledProjects.length > 0 && (
          <div className="bg-slate-800 border-t border-slate-700 p-4">
            <h3 className="text-base font-semibold text-white mb-3">Unscheduled Projects</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
              {unscheduledProjects.map((project) => (
                <div
                  key={project.id}
                  className="p-3 bg-slate-700 border border-slate-600 rounded cursor-pointer hover:border-slate-500 transition-colors"
                  onClick={() => onSelectProject(project)}
                >
                  <div className="flex items-start justify-between mb-1.5">
                    <h4 className="font-medium text-slate-200 text-sm truncate pr-2">{project.name}</h4>
                    <span className={`px-1.5 py-0.5 rounded-full text-xs flex-shrink-0 ${getStatusBadgeColor(project.status)}`}>
                      {project.status.replace('_', ' ')}
                    </span>
                  </div>
                  {project.description && (
                    <p className="text-xs text-slate-400 mb-2 line-clamp-1 truncate">
                      {project.description}
                    </p>
                  )}
                  <div className="flex items-center justify-between text-xs text-slate-500">
                    <span className="truncate">{project.created_by_name}</span>
                    <span className="flex-shrink-0">{project.total_tasks || 0} tasks</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TimelineView;